import { useCallback, useMemo } from 'react'
import { UPDATE_CONTACT_MUTATION } from './graphql'
import { pipelineSource, type WriteContact } from './boardColumnSource'
import { BOARD_STAGES, TREATMENT_PIPELINE_ID, TREATMENT_STAGES } from './treatmentStageCatalog'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useSystemList } from 'app/hooks/useSystemList'
import { gqlMutation } from 'app/lib/graphqlClient'
import { moveListItem } from 'app/lib/listApi'
import type { ContactJson } from 'app/types/entities'
import type { TreatmentLane, TreatmentStages } from 'app/types/treatment'
import { stageIdForName } from 'lib/listMemberships'
import { splitSystemStages } from 'lib/systemListBoard'
import { TREATMENT_LIST, hiddenStatusTerms } from 'lib/systemLists'
import {
  listTreatmentDestinations,
  listTreatmentLanes,
  treatmentLaneOf,
  treatmentPopulation,
} from 'lib/treatmentStages'

const LIST_HIDDEN = hiddenStatusTerms(TREATMENT_LIST)
const NO_TERMS: string[] = []
const OLD_LANES: TreatmentLane[] = BOARD_STAGES.map((stage) => ({
  id: stage.id,
  label: stage.label,
  blurb: stage.blurb,
}))

/** Where the treatment board's stages come from: the old pipeline, or the Unscheduled Treatment list. */
export function useTreatmentStages(): TreatmentStages {
  const listBacked = !!useFeatureToggle(`grouped-navigation`)
  const { list, failed, reload } = useSystemList(TREATMENT_LIST, { enabled: listBacked })

  const writeContact = useCallback<WriteContact>(async (id, input) => {
    const result = await gqlMutation(UPDATE_CONTACT_MUTATION, { id, input })
    if (result.error) throw new Error(result.error.message)
  }, [])
  const source = useMemo(() => pipelineSource(TREATMENT_PIPELINE_ID, writeContact), [writeContact])

  return useMemo<TreatmentStages>(() => {
    if (!listBacked) {
      return {
        listBacked,
        lanes: OLD_LANES,
        exits: [],
        hiddenTerms: NO_TERMS,
        population: undefined,
        destinations: TREATMENT_STAGES,
        bookedStage: undefined,
        list: undefined,
        failed: false,
        reloadList: reload,
        // No membership yet reads as the first working stage, as the board always has.
        stageOf: (card) =>
          source.columnOf({ id: card.id, pipelines: card.pipelines } as ContactJson) ?? OLD_LANES[0].label,
        write: (card, stage) => source.move({ id: card.id, pipelines: card.pipelines } as ContactJson, stage),
      }
    }
    const stages = list ? listTreatmentLanes(list) : []
    const { lanes: laneStages } = splitSystemStages(list?.stages ?? [], TREATMENT_LIST.exitStageNames)
    const laneIds = new Set(laneStages.map((stage) => stage.id))
    const lanes = stages.filter((stage) => laneIds.has(stage.id))
    const exits = stages.filter((stage) => !laneIds.has(stage.id))
    const fallback = list?.stages?.find((stage) => stage.id === list.defaultStageId)?.name ?? lanes[0]?.label ?? ``
    const listDestinations = list ? listTreatmentDestinations(list) : []
    const bookedStage = listDestinations.find((destination) => destination.name === TREATMENT_LIST.bookedStage)
    return {
      listBacked,
      lanes,
      exits,
      hiddenTerms: LIST_HIDDEN,
      population: treatmentPopulation(list),
      destinations: listDestinations.filter((destination) => destination !== bookedStage),
      bookedStage,
      list,
      failed,
      reloadList: reload,
      stageOf: (card) => treatmentLaneOf(card.treatmentStatus, stages, fallback),
      write: async (card, stage) => {
        const stageId = list ? stageIdForName(list, stage) : undefined
        if (!list || !stageId) throw new Error(`Unscheduled Treatment has no status named ${stage}`)
        await moveListItem(list.id, card.id, stageId)
      },
    }
  }, [listBacked, list, failed, reload, source])
}
